import {
    LinkIdContainer,
    processId,
    COMPOSE_ONLY_ATTACHMENT_CLASS_NAME,
} from 'owa-attachment-link-common';
import { FluidOwaSource, isFluidEnabledForSource } from 'owa-fluid-validations';
import { AnchorElementsSource } from 'owa-link-data';
import { ORIGINALSRC_ATTTIBUTE_NAME, SHASH_ATTTIBUTE_NAME } from 'owa-safelinks-evaluator';
import { trace } from 'owa-trace';
import { BeforePasteEvent } from 'roosterjs-editor-types';
import LinkPlugin from '../plugin/LinkPlugin';
import { ANCHOR_TAG_NAME, FLUID_URL_QUERY_SELECTOR, IMG_TAG_NAME } from './constants';
import processAnchorElements from './processAnchorElementsFromHtmlContent';
import { chainSanitizerCallback } from 'roosterjs-editor-dom';

const ALLOWED_ATTRIBUTES_WHEN_PASTE = ['fluid-url', 'owaLinkId'];

export default function handleBeforePasteEvent(
    event: BeforePasteEvent,
    linkPlugin: LinkPlugin,
    linkIdContainer: LinkIdContainer,
    linkIdsWithSpinner: string[],
    targetWindow: Window,
    sxsId: string,
    isCalendar: boolean
) {
    trace.info('LinkPlugin.handleBeforePasteEvent');

    // If the fluid framework / bohemia option is enabled,
    // check for the presence of the fluid-url property on the root of the fragment.
    // If it exists, replace it with an anchor tag.
    if (isFluidEnabledForSource(FluidOwaSource.MailCompose)) {
        const fluidUrlElement = event.fragment.querySelector(FLUID_URL_QUERY_SELECTOR);
        if (fluidUrlElement) {
            const newAnchor = document.createElement(ANCHOR_TAG_NAME);
            newAnchor.href = fluidUrlElement.getAttribute('fluid-url');
            newAnchor.textContent = newAnchor.href;
            fluidUrlElement.parentNode.replaceChild(newAnchor, fluidUrlElement);
        }
    }

    const links = event.fragment.querySelectorAll(ANCHOR_TAG_NAME);
    const images: NodeListOf<HTMLImageElement> = event.fragment.querySelectorAll(IMG_TAG_NAME);
    const isPureLinks: boolean = isPastedTextPureLinks(event.fragment.textContent, links);
    processAnchorElements(
        links,
        null,
        AnchorElementsSource.PasteHtml,
        linkPlugin,
        linkIdContainer,
        linkIdsWithSpinner,
        true /*shouldChangeLinkId*/,
        isPureLinks && images.length === 0,
        targetWindow,
        sxsId,
        isCalendar
    );

    // For all beautiful links, we will set a special attribute for further process as other plugin code may mess with the id.
    const {
        additionalAllowedAttributes,
        attributeCallbacks,
        additionalAllowedCssClasses,
    } = event.sanitizingOption;
    additionalAllowedAttributes.push.apply(
        additionalAllowedAttributes,
        ALLOWED_ATTRIBUTES_WHEN_PASTE
    );
    additionalAllowedCssClasses.push(COMPOSE_ONLY_ATTACHMENT_CLASS_NAME);
    chainSanitizerCallback(attributeCallbacks, 'id', processId);

    // Need to keep shash value for safelink detection
    chainSanitizerCallback(attributeCallbacks, 'shash', processShash);
}

function processShash(value: string, element: HTMLElement, context: Object) {
    return element.getAttribute(SHASH_ATTTIBUTE_NAME);
}

function isPastedTextPureLinks(text: string, allLinks: NodeListOf<HTMLAnchorElement>): boolean {
    const textAfterWhitespaceRemoved: string = text.replace(/\s/g, '');
    let allHrefTogether: string = '';
    for (let i = 0; i < allLinks.length; i++) {
        const link = allLinks[i] as HTMLAnchorElement;
        allHrefTogether += getOriginalSrcOrHref(link);
    }

    return textAfterWhitespaceRemoved.toLowerCase() === allHrefTogether.toLowerCase();
}

function getOriginalSrcOrHref(link: HTMLAnchorElement): string {
    const url: string = link.getAttribute(ORIGINALSRC_ATTTIBUTE_NAME);

    return url?.length > 0 ? url : link.href;
}
